import { useGetExtrasTranslated } from "@/components/ReservationOptions/hooks/useGetExtrasTranslated.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { translateCalculationRule } from "@/helpers/translation/translateCalculationRule.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useGetRPTranslated } from "@/helpers/translation/useGetRPTranslated.ts";
import { ParcelObjectType, RateGroup } from "@/helpers/types/types.ts";
import { useIsMobile } from "@/helpers/useIsMobile.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import cn from "classnames";
import format from "date-fns/format";
import { MouseEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CiCalendar } from "react-icons/ci";
import { FaInfoCircle } from "react-icons/fa";
import { FaCaretRight } from "react-icons/fa6";
import { IoPersonOutline } from "react-icons/io5";
import { PiBedLight, PiTentLight } from "react-icons/pi";
import { RiCaravanLine } from "react-icons/ri";
import { useShallow } from "zustand/react/shallow";

export const AccommodationOverview = (props: {
  cartAccommodation: CartAccommodationItem;
  handleOpenGallery: (rg: RateGroup) => void;
}) => {
  const { cartAccommodation } = props;
  const { calculatedCartData } = useEngineStore(
    useShallow((state) => ({
      calculatedCartData: state.calculatedCartData?.accommodations.find(
        (acc) => acc.identifier === cartAccommodation.id,
      ),
    })),
  );

  const { t } = useTranslation();
  const [showExtras, setShowExtras] = useState(false);
  const { getTranslateRPData } = useGetRPTranslated();
  const { getTranslateRGData } = useGetRGTranslated();
  const getExtrasTranslated = useGetExtrasTranslated();
  const isMobile = useIsMobile();

  const { name: RGName } = useMemo(
    () => getTranslateRGData(cartAccommodation.rateGroup),
    [getTranslateRGData, cartAccommodation.rateGroup],
  );
  const { name: RPName } = useMemo(
    () => getTranslateRPData(cartAccommodation.ratePlan),
    [getTranslateRPData, cartAccommodation.ratePlan],
  );

  const optionalExtrasTotal = useMemo(() => {
    const extras = cartAccommodation.optionalExtras;
    return extras.reduce((acc, extra) => {
      const expenseForExtra = calculatedCartData?.extras.find((cae) => cae.id === extra.id);
      return acc + (expenseForExtra?.total || 0);
    }, 0);
  }, [calculatedCartData?.extras, cartAccommodation.optionalExtras]);

  const renderExtrasList = () => {
    const extras = cartAccommodation.optionalExtras;
    return extras?.map((extra) => {
      const expenseForExtra = calculatedCartData?.extras.find((cae) => cae.id === extra.id);
      const { name: extraName } = getExtrasTranslated(extra);
      const renderCalculationRule = () => {
        if (isMobile) return;
        return `(${translateCalculationRule(extra.calculationRule).toLowerCase()})`;
      };

      return (
        <div key={extra.id} className="accommodation-overview__extras-list--row">
          <p className={"u-p1"}>
            {extraName} {renderCalculationRule()}
          </p>
          <p className={"u-p1"}>
            <>
              {expenseForExtra?.calculatedQuantity || 0} x {formatCurrency(extra.price)} ={" "}
              {formatCurrency(expenseForExtra?.total || 0) || t("error")}
            </>
          </p>
        </div>
      );
    });
  };

  const renderVehicleTypeIcon = () => {
    switch (cartAccommodation.parcelObjectType) {
      case ParcelObjectType.Camper:
      case ParcelObjectType.Caravan:
        return <RiCaravanLine />;
      case ParcelObjectType.Tent:
        return <PiTentLight />;

      default:
        return <PiTentLight />;
    }
  };

  const handleAccommodationInfoClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    props.handleOpenGallery(cartAccommodation.rateGroup);
  };

  return (
    <div className="accommodation-overview">
      <div>
        <div className="accommodation-overview__title--container">
          <div className="accommodation-overview__title" onClick={handleAccommodationInfoClick}>
            <h6>{RGName}</h6>
            <FaInfoCircle size={12} />
          </div>
          <p className="u-h6 u-fw-600">
            {formatCurrency((calculatedCartData?.priceBreakdown?.withDiscount || 0) - optionalExtrasTotal) ||
              t("error")}
          </p>
        </div>
        <p className="accommodation-overview__row">
          <CiCalendar />
          {format(new Date(cartAccommodation.dateRange[0]!), "dd.MM.yyyy")} -{" "}
          {format(new Date(cartAccommodation.dateRange[1]!), "dd.MM.yyyy")}
        </p>
        <p className="accommodation-overview__row">
          <PiBedLight />
          {RPName}
        </p>
        <p className="accommodation-overview__row">
          <IoPersonOutline />
          {t("adultsCount")}: {cartAccommodation.adults.length}
          {cartAccommodation.children.length > 0 && (
            <>
              , {t("childrenCount")}: {cartAccommodation.children.length}
            </>
          )}
        </p>
        {cartAccommodation.parcelObjectType && (
          <p className="accommodation-overview__row">
            {renderVehicleTypeIcon()}
            {t(`campSpecific.${cartAccommodation.parcelObjectType.toLowerCase()}`)}{" "}
            {cartAccommodation.parcelObjectDimensions}
          </p>
        )}
        {cartAccommodation.optionalExtras.length > 0 && (
          <div className="accommodation-overview__extras-button--row">
            <button
              onClick={() => setShowExtras((prev) => !prev)}
              className={cn("u-p0  u-fw-500 c-be-link", {
                "accommodation-overview__extras-button": !showExtras,
                "accommodation-overview__extras-button--active": showExtras,
              })}
            >
              <FaCaretRight />
              <span>
                {t("step1")} ({cartAccommodation.optionalExtras.length})
              </span>
            </button>
            <p className="u-h6 u-fw-600">{formatCurrency(optionalExtrasTotal)}</p>
          </div>
        )}
        {showExtras && <div className="accommodation-overview__extras-list">{renderExtrasList()}</div>}
      </div>
    </div>
  );
};
